import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    IconButton,
    Menu,
    MenuItem,
    CircularProgress
} from '@mui/material';
import { Add as AddIcon, MoreVert as MoreVertIcon, AccountBalance, Visibility, VisibilityOff, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { BankAccount } from '../types/BankAccount';
import { getBankAccounts, addBankAccount, updateBankAccount, deleteBankAccount } from '../firebase/firestoreOperations';
import BankAccountForm from './BankAccountForm';
import { User } from 'firebase/auth';
import { designTokens } from '../theme/designTokens';
import { CardGridSkeleton } from './LoadingSkeleton';

interface BankAccountsProps {
    user: User | null;
}

const BankAccounts: React.FC<BankAccountsProps> = ({ user }) => {
    const intl = useIntl();
    const [accounts, setAccounts] = useState<BankAccount[]>([]);
    const [loading, setLoading] = useState(true);
    const [openForm, setOpenForm] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<BankAccount | undefined>(undefined);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [menuAccountId, setMenuAccountId] = useState<string | null>(null);
    const [showBalances, setShowBalances] = useState(false);

    useEffect(() => {
        if (user) {
            fetchAccounts();
        }
    }, [user]);

    const fetchAccounts = async () => {
        if (!user) return;
        try {
            setLoading(true);
            const fetchedAccounts = await getBankAccounts(user.uid);
            setAccounts(fetchedAccounts);
        } catch (error) {
            console.error("Error fetching bank accounts:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddAccount = () => {
        setSelectedAccount(undefined);
        setOpenForm(true);
    };

    const handleEditAccount = (account: BankAccount) => {
        setSelectedAccount(account);
        setOpenForm(true);
        handleCloseMenu();
    };

    const handleDeleteAccount = async (id: string) => {
        if (!window.confirm(intl.formatMessage({ id: 'confirmDeleteAccount', defaultMessage: 'Are you sure you want to delete this account?' }))) {
            return;
        }
        try {
            if (!user) return;
            await deleteBankAccount(id, user.uid);
            setAccounts(prev => prev.filter(acc => acc.id !== id));
        } catch (error) {
            console.error("Error deleting bank account:", error);
        }
        handleCloseMenu();
    };

    const handleSaveAccount = async (accountData: Omit<BankAccount, 'id'> | BankAccount) => {
        if (!user) return;
        try {
            if ('id' in accountData) {
                await updateBankAccount(accountData as BankAccount);
                setAccounts(prev => prev.map(acc => acc.id === (accountData as BankAccount).id ? accountData as BankAccount : acc));
            } else {
                const newAccount = await addBankAccount({
                    ...accountData,
                    order: accounts.length
                }, user.uid);
                setAccounts(prev => [...prev, newAccount]);
            }
        } catch (error) {
            console.error("Error saving bank account:", error);
        }
    };

    const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, accountId: string) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuAccountId(accountId);
    };

    const handleCloseMenu = () => {
        setMenuAnchorEl(null);
        setMenuAccountId(null);
    };

    const handleMoveAccount = async (id: string, direction: 'up' | 'down') => {
        const index = accounts.findIndex(acc => acc.id === id);
        if (index === -1) return;

        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= accounts.length) return;

        const newAccounts = [...accounts];
        const temp = newAccounts[index];
        newAccounts[index] = newAccounts[newIndex];
        newAccounts[newIndex] = temp;

        // Update orders
        const updatedAccounts = newAccounts.map((acc, idx) => ({
            ...acc,
            order: idx
        }));

        setAccounts(updatedAccounts);
        handleCloseMenu();

        try {
            // Persist changes to Firestore
            await Promise.all(updatedAccounts.map(acc => updateBankAccount(acc)));
        } catch (error) {
            console.error("Error updating account order:", error);
            // Optionally revert local state on error
            fetchAccounts();
        }
    };

    const totalBalance = accounts.reduce((sum, acc) => sum + acc.balance, 0);

    if (loading) {
        return <CardGridSkeleton />;
    }

    return (
        <Box sx={{
            p: designTokens.spacing.lg,
            maxWidth: '1200px',
            mx: 'auto'
        }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" sx={{ fontWeight: designTokens.typography.fontWeight.bold }}>
                    <FormattedMessage id="bankAccounts" defaultMessage="Bank Accounts" />
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                    <IconButton
                        onClick={() => setShowBalances(!showBalances)}
                        sx={{ color: designTokens.colors.primary.main }}
                    >
                        {showBalances ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddAccount}
                        sx={{
                            borderRadius: designTokens.borderRadius.md,
                            background: designTokens.colors.primary.gradient,
                            textTransform: 'none',
                            px: 3,
                            boxShadow: designTokens.shadows.md
                        }}
                    >
                        <FormattedMessage id="addAccount" defaultMessage="Add Account" />
                    </Button>
                </Box>
            </Box>

            <Card sx={{
                mb: 6,
                background: designTokens.colors.primary.gradient,
                color: '#fff',
                borderRadius: designTokens.borderRadius.xl,
                boxShadow: designTokens.shadows.xl,
                position: 'relative',
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.1)',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 300,
                    height: 300,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
                }
            }}>
                <CardContent sx={{ p: 5, position: 'relative', zIndex: 1 }}>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <AccountBalance sx={{ opacity: 0.8 }} />
                        <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: designTokens.typography.fontWeight.medium, textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.8rem' }}>
                            <FormattedMessage id="totalBalance" defaultMessage="Total Balance" />
                        </Typography>
                    </Box>
                    <Typography variant="h2" sx={{
                        fontWeight: designTokens.typography.fontWeight.bold,
                        textShadow: '0 4px 10px rgba(0,0,0,0.3)',
                        fontFamily: '"JetBrains Mono", "Roboto Mono", monospace',
                    }}>
                        {showBalances ? `€ ${totalBalance.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '€ ****'}
                    </Typography>
                </CardContent>
            </Card>

            <Grid container spacing={4}>
                {accounts.map(account => (
                    <Grid item xs={12} sm={6} md={4} key={account.id}>
                        <Card sx={{
                            height: '220px',
                            borderRadius: '20px',
                            background: `linear-gradient(135deg, ${account.color || designTokens.colors.primary.main} 0%, ${designTokens.colors.background.default} 100%)`,
                            position: 'relative',
                            overflow: 'hidden',
                            boxShadow: designTokens.shadows.xl,
                            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                            '&:hover': {
                                transform: 'translateY(-10px) rotateX(5deg) rotateY(-5deg)',
                                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                            },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: '-50%',
                                left: '-50%',
                                width: '200%',
                                height: '200%',
                                background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                                transform: 'rotate(30deg)',
                                pointerEvents: 'none',
                            }
                        }}>
                            <CardContent sx={{ h: '100%', p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#fff', position: 'relative', zIndex: 1 }}>
                                <Box display="flex" justifyContent="flex-end" alignItems="center">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleOpenMenu(e, account.id)}
                                        sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                </Box>

                                <Box>
                                    <Typography variant="h4" sx={{
                                        fontFamily: '"JetBrains Mono", "Roboto Mono", monospace',
                                        fontWeight: 'bold',
                                        letterSpacing: '2px',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                        mb: 1
                                    }}>
                                        {showBalances ? `€ ${account.balance.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '€ ****'}
                                    </Typography>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-end">
                                        <Box>
                                            <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '1px' }}>
                                                Account Holder
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: designTokens.typography.fontWeight.semibold, letterSpacing: '1px' }}>
                                                {account.name}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    opacity: 0.8,
                                                    textTransform: 'uppercase',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 'bold',
                                                    background: 'rgba(255,255,255,0.2)',
                                                    px: 1,
                                                    py: 0.5,
                                                    borderRadius: '4px',
                                                    backdropFilter: 'blur(4px)'
                                                }}
                                            >
                                                <FormattedMessage id={`accountType.${account.type || 'checking'}`} />
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>

                            {/* Abstract card texture elements */}
                            <Box sx={{
                                position: 'absolute',
                                bottom: -20,
                                right: -20,
                                width: 100,
                                height: 100,
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.1)',
                                backdropFilter: 'blur(5px)'
                            }} />
                            <Box sx={{
                                position: 'absolute',
                                bottom: 20,
                                right: 60,
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.05)',
                                backdropFilter: 'blur(2px)'
                            }} />
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleCloseMenu}
            >
                <MenuItem onClick={() => {
                    const account = accounts.find(acc => acc.id === menuAccountId);
                    if (account) handleEditAccount(account);
                }}>
                    <FormattedMessage id="edit" defaultMessage="Edit" />
                </MenuItem>
                <MenuItem
                    disabled={accounts.findIndex(acc => acc.id === menuAccountId) === 0}
                    onClick={() => menuAccountId && handleMoveAccount(menuAccountId, 'up')}
                >
                    <ArrowUpward fontSize="small" sx={{ mr: 1 }} />
                    Subir
                </MenuItem>
                <MenuItem
                    disabled={accounts.findIndex(acc => acc.id === menuAccountId) === accounts.length - 1}
                    onClick={() => menuAccountId && handleMoveAccount(menuAccountId, 'down')}
                >
                    <ArrowDownward fontSize="small" sx={{ mr: 1 }} />
                    Descer
                </MenuItem>
                <MenuItem onClick={() => menuAccountId && handleDeleteAccount(menuAccountId)}>
                    <FormattedMessage id="delete" defaultMessage="Delete" />
                </MenuItem>
            </Menu>

            <BankAccountForm
                open={openForm}
                onClose={() => setOpenForm(false)}
                onSave={handleSaveAccount}
                initialData={selectedAccount}
            />
        </Box>
    );
};

export default BankAccounts;
